import { apiRoutes, buildApiRoute } from "../../config/apiRoutes.config.js";

export const CMTS_API = {
  auth: {
    login: buildApiRoute(apiRoutes.auth.login),
    refresh: buildApiRoute(apiRoutes.auth.refresh),
    logout: buildApiRoute(apiRoutes.auth.logout),
    me: buildApiRoute(apiRoutes.auth.me),
    changePassword: "/auth/change-password",
    requestOtp: "/auth/otp/request",
    verifyOtp: "/auth/otp/verify",
    sessions: "/auth/sessions",
    revokeSession: (sessionId: string) => `/auth/sessions/${sessionId}/revoke`,
    myPermissions: buildApiRoute(apiRoutes.auth.myPermissions),
    myOrganizationScope: "/auth/my-organization-scope",
    myMenu: buildApiRoute(apiRoutes.auth.myMenu),
    quickAccessUsers: buildApiRoute(apiRoutes.auth.quickAccessUsers),
  },
  complaints: {
    list: buildApiRoute(apiRoutes.complaints.list),
    create: buildApiRoute(apiRoutes.complaints.create),
    detail: (id: string) => buildApiRoute(apiRoutes.complaints.get, { id }),
    byCode: (code: string) => `/complaints/by-code/${encodeURIComponent(code)}`,
    allowedActions: (id: string) => buildApiRoute(apiRoutes.complaints.allowedActions, { id }),
    workflowAction: (id: string, actionCode: string) => buildApiRoute(apiRoutes.complaints.workflowAction, { id, actionCode }),
    comments: (complaintId: string) => buildApiRoute(apiRoutes.complaints.comments.list, { id: complaintId }),
    rca: (complaintId: string) => buildApiRoute(apiRoutes.rca.list, { complaintId }),
    rcaQueue: () => buildApiRoute(apiRoutes.rca.queue, {}),
    completeRca: (complaintId: string, rcaId: string) => buildApiRoute(apiRoutes.rca.complete, { complaintId, id: rcaId }),
    capa: (complaintId: string) => buildApiRoute(apiRoutes.capa.list, { complaintId }),
    completeCapa: (complaintId: string, capaId: string) => buildApiRoute(apiRoutes.capa.complete, { complaintId, id: capaId }),
    prepareVerification: (complaintId: string) => `/complaints/${complaintId}/verification/prepare`,
    approveVerification: (complaintId: string, verificationId: string) => `/complaints/${complaintId}/verification/${verificationId}/approve`,
    rejectVerification: (complaintId: string, verificationId: string) => `/complaints/${complaintId}/verification/${verificationId}/reject`,
    tasks: (complaintId: string) => buildApiRoute(apiRoutes.complaints.tasks.list, { id: complaintId }),
    messages: (complaintId: string) => `/complaints/${complaintId}/messages`,
  },
  evidence: {
    list: (complaintId: string) => buildApiRoute(apiRoutes.complaints.evidence.list, { id: complaintId }),
    metadata: (complaintId: string) => `/complaints/${complaintId}/evidence/metadata`,
    uploadForComplaint: (complaintId: string) => buildApiRoute(apiRoutes.complaints.evidence.upload, { id: complaintId }),
    downloadForComplaint: (complaintId: string, evidenceId: string) => `/complaints/${complaintId}/evidence/${evidenceId}/download`,
    deleteForComplaint: (complaintId: string, evidenceId: string) => `/complaints/${complaintId}/evidence/${evidenceId}`,
    upload: "/evidence/upload",
    detail: (id: string) => `/evidence/${id}`,
    download: (id: string) => `/evidence/${id}/download`,
    downloadToken: (id: string) => `/evidence/${id}/download-token`,
    packageExport: "/evidence/package-export",
    packageStatus: (id: string) => `/evidence/package-export/${id}`,
    packageDownload: (id: string) => `/evidence/package-export/${id}/download`,
  },
  evidenceAdmin: {
    policies: "/evidence-administration/policies",
    policy: (id: string) => `/evidence-administration/policies/${id}`,
    validatePolicy: (id: string) => `/evidence-administration/policies/${id}/validate`,
    activatePolicy: (id: string) => `/evidence-administration/policies/${id}/activate`,
    storageHealth: "/evidence-administration/storage-health",
    scanHealth: "/evidence-administration/scan-health",
    scanResults: "/evidence-administration/scan-results",
    rescan: (id: string) => `/evidence-administration/evidence/${id}/rescan`,
    quarantine: (id: string) => `/evidence-administration/evidence/${id}/quarantine`,
    release: (id: string) => `/evidence-administration/evidence/${id}/release`,
  },
  tasks: {
    create: "/tasks",

    createForComplaint: (
      complaintId: string,
    ) =>
      `/complaints/${encodeURIComponent(
        complaintId,
      )}/tasks`,

    my: "/tasks/my",
    accept: (id: string) =>
      `/tasks/${id}/accept`,
    acknowledge: (id: string) =>
      `/tasks/${id}/acknowledge`,
    assign: (id: string) =>
      `/tasks/${id}/assign`,
    complete: (id: string) =>
      `/tasks/${id}/complete`,
    comment: (id: string) =>
      `/tasks/${id}/comment`,
    snooze: (id: string) =>
      `/tasks/${id}/snooze`,
    reopen: (id: string) =>
      `/tasks/${id}/reopen`,
  },
  messages: {
  threads:
    "/messages/threads",

  thread: (
    threadId: string,
  ) =>
    `/messages/threads/${encodeURIComponent(
      threadId,
    )}`,

  addToThread: (
    threadId: string,
  ) =>
    `/messages/threads/${encodeURIComponent(
      threadId,
    )}/messages`,

  markRead: (
    threadId: string,
  ) =>
    `/messages/threads/${encodeURIComponent(
      threadId,
    )}/mark-read`,

  markUnread: (
    threadId: string,
  ) =>
    `/messages/threads/${encodeURIComponent(
      threadId,
    )}/mark-unread`,

  archive: (
    threadId: string,
  ) =>
    `/messages/threads/${encodeURIComponent(
      threadId,
    )}/archive`,

  createTask: (
    threadId: string,
  ) =>
    `/messages/threads/${encodeURIComponent(
      threadId,
    )}/create-task`,

  delete: (
    messageId: string,
  ) =>
    `/messages/${encodeURIComponent(
      messageId,
    )}/delete`,
},
  notifications: {
    my: "/notifications/my",
    markRead: (id: string) => `/notifications/${encodeURIComponent(id)}/mark-read`,
    markUnread: (id: string) => `/notifications/${encodeURIComponent(id)}/mark-unread`,
    acknowledge: (id: string) => `/notifications/${encodeURIComponent(id)}/acknowledge`,
    archive: (id: string) => `/notifications/${encodeURIComponent(id)}/archive`,
    action: (id: string) => `/notifications/${encodeURIComponent(id)}/action`,
    createTask: (id: string) => `/notifications/${encodeURIComponent(id)}/task`,
    templates: "/notifications/templates",
    dispatchPending: "/notifications/dispatch-pending",
    channelHealth: "/notifications/channels/health",
  },
  alerts: {
    create: "/alerts",
    my: "/alerts/my",
    acknowledge: (id: string) => `/alerts/${id}/acknowledge`,
    resolve: (id: string) => `/alerts/${id}/resolve`,
  },
  notificationProviders: {
    list: "/notification-providers",
    create: "/notification-providers",
    detail: (id: string) => `/notification-providers/${id}`,
    validate: (id: string) => `/notification-providers/${id}/validate`,
    testDispatch: (id: string) => `/notification-providers/${id}/test-dispatch`,
    activate: (id: string) => `/notification-providers/${id}/activate`,
    deactivate: (id: string) => `/notification-providers/${id}/deactivate`,
    health: (id: string) => `/notification-providers/${id}/health`,
    channelHealth: "/notifications/channel-health",
    attempts: "/notification-providers/dispatch-attempts",
    retryAttempt: (id: string) => `/notification-providers/dispatch-attempts/${id}/retry`,
  },
  sla: {
    create: "/sla",
    profiles: "/sla/profiles",
    rules: "/sla/rules",
    trackers: "/sla/trackers",
    calendarProfiles: "/admin/calendars",
    evaluate: "/sla/evaluate",
    pause: (id: string) => `/sla/${id}/pause`,
    resume: (id: string) => `/sla/${id}/resume`,
  },
  escalation: {
    create: "/escalations",
    evaluate: "/escalation/evaluate",
    queue: "/escalations",
    acknowledge: (id: string) => `/escalations/${encodeURIComponent(id)}/acknowledge`,
    resolve: (id: string) => `/escalations/${encodeURIComponent(id)}/resolve`,
    rules: "/admin/escalation-rules",
  },
  reports: {
    catalog:
      buildApiRoute(
        apiRoutes.reports.catalog,
      ),

    definition: (
      code: string,
    ) =>
      `/reports/${encodeURIComponent(
        code,
      )}`,

    preview:
      buildApiRoute(
        apiRoutes.reports.preview,
      ),

    resultDetail: (
      id: string,
    ) =>
      `/reports/results/${encodeURIComponent(
        id,
      )}`,

    operationSpineChain:
      "/reports/operation-spine-chain",

    auditTrail:
      "/reports/audit-trail",

    scheduledRun:
      "/reports/scheduled/run",

    filterOptions:
      "/reports/filter-options",

    /*
     * Report Export History controller:
     * /api/cmts/report-exports
     */
    exports:
      "/report-exports",

    exportHistory:
      "/report-exports",

    exportDetail: (
      id: string,
    ) =>
      `/report-exports/${encodeURIComponent(
        id,
      )}`,

    exportRetry: (
      id: string,
    ) =>
      `/report-exports/${encodeURIComponent(
        id,
      )}/retry`,

    exportCancel: (
      id: string,
    ) =>
      `/report-exports/${encodeURIComponent(
        id,
      )}/cancel`,

    exportDownload: (
      id: string,
    ) =>
      `/report-exports/${encodeURIComponent(
        id,
      )}/download`,

    /*
     * Typed report export route.
     * இது separate typed-report workflow.
     */
    typedDownload: (
      id: string,
    ) =>
      `/report-exports/typed/${encodeURIComponent(
        id,
      )}/download`,

    typedQuery: (
      code: string,
    ) =>
      `/reports/${encodeURIComponent(
        code,
      )}/query`,

    typedExport: (
      code: string,
    ) =>
      `/report-exports/${encodeURIComponent(
        code,
      )}`,
  },
  dashboards: {
    management: buildApiRoute(apiRoutes.dashboards.management),
    department: "/dashboards/department",
    personal: buildApiRoute(apiRoutes.dashboards.personal),
    customer: "/dashboards/customer",
    supplier: "/dashboards/supplier",
    quality: "/dashboards/quality",
    query: "/dashboards/query",
    kpi: (kpiCode: string) => `/dashboards/kpi/${encodeURIComponent(kpiCode)}`,
    drilldown: buildApiRoute(apiRoutes.dashboards.drilldown),
    export: buildApiRoute(apiRoutes.dashboards.export),
    configuration: "/dashboards/configuration",
    validateConfiguration: "/dashboards/configuration/validate",
  },
  configurationGovernance: {
    versions: "/configuration-governance/versions",
    drafts: "/configuration-governance/drafts",
    draft: (id: string) => `/configuration-governance/drafts/${id}`,
    export: "/configuration-governance/export",
    importDraft: "/configuration-governance/import-draft",
    validateDraft: (id: string) => `/configuration-governance/drafts/${id}/validate`,
    submitDraft: (id: string) => `/configuration-governance/drafts/${id}/submit`,
    approveDraft: (id: string) => `/configuration-governance/drafts/${id}/approve`,
    rejectDraft: (id: string) => `/configuration-governance/drafts/${id}/reject`,
    scheduleDraft: (id: string) => `/configuration-governance/drafts/${id}/schedule`,
    activateDraft: (id: string) => `/configuration-governance/drafts/${id}/activate`,
    rollbackVersion: (id: string) => `/configuration-governance/versions/${id}/rollback`,
    audit: "/configuration-governance/audit",
    materializationLogs: "/configuration-governance/materialization-logs",
    rollbackSnapshots: "/configuration-governance/rollback-snapshots",
    dependencyCheck: (id: string) => `/configuration-governance/drafts/${id}/dependency-check`,
  },
  masterDependencies: {
    detail: (masterType: string, id: string) => `/master-dependencies/${encodeURIComponent(masterType)}/${id}`,
    check: "/master-dependencies/check",
    checkDelete: "/master-dependencies/check-delete",
    checkDeactivate: "/master-dependencies/check-deactivate",
    checkImportDraft: "/master-dependencies/check-import-draft",
    checkActivation: "/master-dependencies/check-activation",
    checkRollback: "/master-dependencies/check-rollback",
    checkSchedule: "/master-dependencies/check-schedule",
    checkReadiness: "/master-dependencies/check-readiness",
    checkCircular: "/master-dependencies/check-circular",
    remediation: "/master-dependencies/remediation",
  },
  masters: {
    users: buildApiRoute(apiRoutes.users.list),
    registry: "/admin/master-registry",
    list: (masterType: string) => buildApiRoute(apiRoutes.adminMasterData.list, { masterType }),
    item: (masterType: string, id: string) => buildApiRoute(apiRoutes.adminMasterData.get, { masterType, id }),
    validate: (masterType: string) => buildApiRoute(apiRoutes.adminMasterData.validate, { masterType }),
    schema: (masterType: string) => buildApiRoute(apiRoutes.adminMasterData.schema, { masterType }),
    // Operational forms and work queues must not depend on an admin-only master-data route.
    // The lifecycle endpoint exposes the same governed dropdown projection with
    // LIFECYCLE_REFERENCE.READ and never exposes master-data mutation operations.
    // IMPORTANT: the backend's LifecycleDropdownMasterTypes whitelist (CanonicalMasterDataAdminService.cs)
    // must include every master type passed through this helper, or callers get
    // "Master type 'X' is not exposed through the lifecycle reference contract." (see the widened
    // whitelist there for: departments, products, processes, confidentiality-level, sla-state, tenant,
    // user, role, permission, report-definition, dashboard-kpi, workflow-profile/-action/-transition,
    // organization-unit-type).
    dropdown: (masterType: string) => buildApiRoute(apiRoutes.lifecycleReference.dropdownOptions, { masterType }),
    dependencyCheck: (masterType: string, id: string) => buildApiRoute(apiRoutes.adminMasterData.dependencyCheck, { masterType, id }),
    activate: (masterType: string, id: string) => buildApiRoute(apiRoutes.adminMasterData.activate, { masterType, id }),
    deactivate: (masterType: string, id: string) => buildApiRoute(apiRoutes.adminMasterData.deactivate, { masterType, id }),
    delete: (masterType: string, id: string) => buildApiRoute(apiRoutes.adminMasterData.delete, { masterType, id }),
  },
  runtimeContract: {
    routes: "/runtime-contract/routes",
    schemas: "/runtime-contract/schemas",
    swaggerStatus: "/runtime-contract/swagger-status",
    frontendContractStatus: "/runtime-contract/frontend-contract-status",
  },
  audit: {
    logs: buildApiRoute(apiRoutes.auditLogs.list),
    detail: (id: string) => `/audit-logs/${encodeURIComponent(id)}`,
    exportHistory: "/audit-logs/export-history",
    exportAudit: "/audit-logs/export",
    securityLogs: buildApiRoute(apiRoutes.securityLogs.list),
  },
  security: {
    logs: buildApiRoute(apiRoutes.securityLogs.list),
    detail: (id: string) => `/security-logs/${encodeURIComponent(id)}`,
    markReviewed: (id: string) => `/security-logs/${encodeURIComponent(id)}/reviewed`,
    createAlert: (id: string) => `/security-logs/${encodeURIComponent(id)}/alert`,
    accessDecisions: "/security/access-decisions",
    blockedActions: "/security/blocked-actions",
    exportSecurity: "/security-logs/export",
  },
  operations: {
    readinessChecks: "/operations/readiness/checks",
    readinessResults: "/operations/readiness/results",
    runReadinessCheck: (id: string) => `/operations/readiness/checks/${encodeURIComponent(id)}/run`,
    runAllReadinessChecks: "/operations/readiness/run-all",
    backendEndpoints: "/operations/backend-endpoints",
    probeEndpoint: (id: string) => `/operations/backend-endpoints/${encodeURIComponent(id)}/probe`,
    probeModule: "/operations/backend-endpoints/module-probe",
    updateEndpointStatus: (id: string) => `/operations/backend-endpoints/${encodeURIComponent(id)}/status`,
    systemHealthSignals: "/operations/system-health",
    systemOperationRuns: "/operations/runs",
    retryOperation: (id: string) => `/operations/runs/${encodeURIComponent(id)}/retry`,
    cancelOperation: (id: string) => `/operations/runs/${encodeURIComponent(id)}/cancel`,
    offlineOutbox: "/operations/outbox",
    replayOutbox: (id: string) => `/operations/outbox/${encodeURIComponent(id)}/replay`,
    failureCenter: "/operations/failure-center",
    markFailureReviewed: (id: string) => `/operations/failure-center/${encodeURIComponent(id)}/reviewed`,
    createRemediationTask: "/operations/remediation-task",
  },

  access: {
    effective: "/access/effective",
    userPermissions: (userId: string) => `/access/permissions/${userId}`,
  },
  authAdmin: {
    changePassword: "/auth/change-password",
    myOrganizationScope: "/auth/my-organization-scope",
    otpRequest: "/auth/otp/request",
    otpVerify: "/auth/otp/verify",
    sessions: "/auth/sessions",
    revokeSession: (sessionId: string) => `/auth/sessions/${sessionId}/revoke`,
  },
  reference: {
    bootstrap: "/reference/bootstrap",
    types: "/reference/types",
    values: "/reference/values",
    categories: "/categories",
    tags: "/tags",
    roles: "/roles",
    permissions: "/permissions",
    users: buildApiRoute(apiRoutes.users.list),
    fieldDefinitions: "/field-definitions",
    fieldSchemas: "/field-schemas",
    validateFieldSchemas: "/field-schemas/validate",
    organizationTree: "/organization-units/tree",
    deactivateOrganizationUnit: (id: string) => `/organization-units/${id}/deactivate`,
    sodPolicies: "/sod/policies",
    routing: "/routing",
  },
  adminCalendars: {
    list: "/calendar/calendars",
    create: "/calendar/calendars",
    update: (calendarCode: string) => `/calendar/calendars/${encodeURIComponent(calendarCode)}`,
    deactivate: (calendarCode: string) => `/calendar/calendars/${encodeURIComponent(calendarCode)}/deactivate`,
    holidayList: "/calendar/holidays",
    createHoliday: "/calendar/holidays",
    updateHoliday: (id: string) => `/calendar/holidays/${encodeURIComponent(id)}`,
    deactivateHoliday: (id: string) => `/calendar/holidays/${encodeURIComponent(id)}/deactivate`,
    shiftList: "/calendar/shifts",
    createShift: "/calendar/shifts",
    updateShift: (id: string) => `/calendar/shifts/${encodeURIComponent(id)}`,
    deactivateShift: (id: string) => `/calendar/shifts/${encodeURIComponent(id)}/deactivate`,
    checkWorkingTime: "/calendar/working-time/check",
    addBusinessDuration: "/calendar/duration/add",
    holidays: (id: string) => `/admin/calendars/${id}/holidays`,
    shifts: (id: string) => `/admin/calendars/${id}/shifts`,
    workingDays: (id: string) => `/admin/calendars/${id}/working-days`,
    calculateDueDate: "/calendar/due-date/calculate",
    addDuration: (calendarProfileId: string) => `/calendar/${calendarProfileId}/add-duration`,
    isWorkingTime: (calendarProfileId: string) => `/calendar/${calendarProfileId}/is-working-time`,
  },
  adminScheduledJobs: {
    list: "/admin/scheduled-jobs",
    save: "/admin/scheduled-jobs",
    runNow: (id: string) => `/admin/scheduled-jobs/${id}/run-now`,
    runs: (id: string) => `/admin/scheduled-jobs/${id}/runs`,
    runtimeList: "/scheduled-jobs",
    runtimeSave: "/scheduled-jobs",
    start: (id: string) => `/scheduled-jobs/${id}/start`,
    completeRun: (runId: string) => `/scheduled-jobs/runs/${runId}/complete`,
  },
  reminders: {
    evaluateDue: "/reminders/evaluate-due",
    send: (id: string) => `/reminders/${id}/send`,
  },
  enterpriseEvents: {
    publish: "/events",
    processPending: "/events/process-pending",
    subscriptions: "/events/subscriptions",
  },
  health: {
    apiContract: "/health/api-contract",
    integrity: "/health/integrity",
    sourceCompletionReview: "/health/source-completion-review",
    strictClosure: "/health/strict-closure",
  },
  migration: {
    legacyRun: "/migration/legacy/run",
  },
  legacyConfiguration: {
    validateTenant: "/admin/configuration/validate",
    versions: "/admin/configuration/versions",
    createVersion: "/admin/configuration/versions",
    validateVersion: (id: string) => `/admin/configuration/versions/${id}/validate`,
    submitVersion: (id: string) => `/admin/configuration/versions/${id}/submit`,
    approveVersion: (id: string) => `/admin/configuration/versions/${id}/approve`,
    rejectVersion: (id: string) => `/admin/configuration/versions/${id}/reject`,
    scheduleVersion: (id: string) => `/admin/configuration/versions/${id}/schedule`,
    activateVersion: (id: string) => `/admin/configuration/versions/${id}/activate`,
    rollbackVersion: (id: string) => `/admin/configuration/versions/${id}/rollback`,
    dependencies: (entityType: string, entityId: string) => `/admin/configuration/dependencies/${encodeURIComponent(entityType)}/${entityId}`,
    export: "/admin/configuration/export",
    importDryRun: "/admin/configuration/import/dry-run",
    importApply: "/admin/configuration/import/apply-to-draft",
    seedDryRun: "/admin/configuration/seed-import/dry-run",
  },
  reportsLegacy: {
    complaintRegister: "/reports/complaint-register",
    slaBreach: "/reports/sla-breach",
    ageing: "/reports/ageing",
    openComplaints: "/reports/open-complaints",
    nearBreach: "/reports/near-breach",
    ownerWise: "/reports/owner-wise",
    departmentWise: "/reports/department-wise",
    categoryWise: "/reports/category-wise",
    severityWise: "/reports/severity-wise",
    sourceWise: "/reports/source-wise",
    plantWise: "/reports/plant-wise",
    customer: "/reports/customer",
    supplier: "/reports/supplier",
    customerComplaints: "/reports/customer-complaints",
    supplierComplaints: "/reports/supplier-complaints",
    rcaCapaLinkage: "/reports/rca-capa-linkage",
    closureVerification: "/reports/closure-verification",
    repeat: "/reports/repeat",
    repeatComplaints: "/reports/repeat-complaints",
    reopen: "/reports/reopen",
    auditLog: "/reports/audit-log",
    managementSummary: "/reports/management-summary",
    export: "/reports/export",
    configurations: "/reports/configurations",
    typed: (code: string) => `/reports/typed/${encodeURIComponent(code)}`,
  },
  operationalReadinessDeep: {
    completenessChecklist: "/operational-readiness/completeness-checklist",
    operationalReadinessReport: "/operational-readiness/operational-readiness-report",
    lifecycleScenarios: "/operational-readiness/lifecycle-scenarios",
    typedReportContracts: "/operational-readiness/reports/typed-contracts",
    normalizeActionPayload: "/operational-readiness/actions/normalize-payload",
    classifyDomain: "/operational-readiness/classification/domain",
    calculateDashboard: "/operational-readiness/dashboard/calculate",
    evaluateEvidenceDownload: "/operational-readiness/evidence/evaluate-download",
    evaluateEvidencePackageExport: "/operational-readiness/evidence/evaluate-package-export",
    validateEvidenceSecurity: "/operational-readiness/evidence/validate-security",
    evaluateMessageVisibility: "/operational-readiness/messages/evaluate-visibility",
    notificationChannelHealth: "/operational-readiness/notifications/channel-health",
    dispatchNotification: "/operational-readiness/notifications/dispatch",
    evaluateSla: "/operational-readiness/sla/evaluate",
    evaluateClosureReadiness: "/operational-readiness/workflow/evaluate-closure-readiness",
    evaluateTransition: "/operational-readiness/workflow/evaluate-transition",
    reportByCode: (code: string) => `/operational-readiness/reports/${encodeURIComponent(code)}`,
  },
  readiness: {
    lifecycleScenarios: "/operational-readiness/lifecycle-scenarios",
    report: "/operational-readiness/operational-readiness-report",
    securityValidation: "/operational-readiness/evidence/validate-security",
    dashboardCalculation: "/operational-readiness/dashboard/calculate",
  },
} as const;

export const TYPED_REPORT_CODES = ["complaint-register", "sla-breach", "sla-near-breach", "ageing", "department-performance", "customer", "supplier", "customer-complaints", "supplier-complaints", "rca-capa", "rca-capa-linkage", "capa-effectiveness", "closure-verification", "escalation", "audit-activity", "security-events", "system-operations", "users", "master-data", "workflow"] as const;
export type TypedReportCode = typeof TYPED_REPORT_CODES[number];
